import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import {
  map,
  switchMap,
  tap,
  catchError,
  withLatestFrom
} from 'rxjs/operators';
import { of } from 'rxjs';
import { NzNotificationService } from 'ng-zorro-antd/notification';

import * as TaskActions from './tasks.actions';
import { APIService } from '../../services/api/api.service';
import { select, Store } from '@ngrx/store';
import * as fromTasks from './tasks.reducer';
import * as TasksSelectors from './tasks.selectors';

@Injectable({
  providedIn: 'root'
})
export class TaskEffects {
  /* -------------------------------- Get Tasks ------------------------------- */

  getTasks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.getTasks),
      withLatestFrom(this.store.pipe(select(TasksSelectors.selectFilters))),
      switchMap(([action, stateFilters]) => {
        const loadType = action.loadType;
        const offset =
          loadType === 'older' ? stateFilters.offset + stateFilters.limit : 0;
        const params = {
          ...action.filters,
          limit: stateFilters.limit,
          offset: offset
        };
        return this.apiService.getTasks(params).pipe(
          tap(tasksResp => {
            if (loadType === 'older' && !tasksResp.hasMore) {
              this.notification.info('Info', 'No more Tasks to load');
            }
          }),
          map(tasksResp =>
            TaskActions.getTasksSuccess({ tasksResp, loadType })
          ),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error loading Tasks'
            );
            return of(TaskActions.getTasksFailure({ error }));
          })
        );
      })
    )
  );

  /* ----------------------------- Get Task By Id ----------------------------- */

  getTaskById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.getTaskById),
      switchMap(action =>
        this.apiService.getTaskById(action.id).pipe(
          map(task => TaskActions.getTaskByIdSuccess({ task })),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              `There was an error getting Task ${action.id}`
            );
            return of(TaskActions.getTaskByIdFailure({ error }));
          })
        )
      )
    )
  );

  /* ------------------------------- Create Task ------------------------------ */

  createTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.createTask),
      switchMap(action =>
        this.apiService.createTask(action.request).pipe(
          tap(() => {
            this.notification.success('Success', 'Task Created');
          }),
          switchMap(res => [
            TaskActions.createTaskSuccess({ task: { ...res } }),
            TaskActions.closeCreateTask()
          ]),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error creating the Task.'
            );
            return of(TaskActions.createTaskFailure({ error }));
          })
        )
      )
    )
  );
  /* ----------------------------- updateCRMUser ---------------------------- */

  updateTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.updateTask),
      switchMap(action =>
        this.apiService.updateTask(action.id, action.task).pipe(
          tap(() => {
            this.notification.success('Success', 'Task updated');
          }),
          map(task => TaskActions.updateTaskSuccess({ task })),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error updating the Task.'
            );
            return of(TaskActions.updateTaskFailure({ error }));
          })
        )
      )
    )
  );

  /* ---------------------------- Delete CRM user --------------------------- */

  deleteTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TaskActions.deleteTask),
      switchMap(action =>
        this.apiService.deleteTask(action.id).pipe(
          tap(() => {
            this.notification.success('Success', 'Task deleted');
          }),
          map(id => TaskActions.deleteTaskSuccess(id)),
          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error deleting the Task.'
            );
            return of(TaskActions.deleteTaskFailure({ error }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private apiService: APIService,
    private notification: NzNotificationService,
    private readonly store: Store<fromTasks.TaskPartialState>
  ) {}
}
